import { useState, useCallback, useRef, useEffect } from 'react';
import {HttpMethod, initFetch} from "../utils/fetcher";

const useSearch = (apiEndpoint, debounceDelay = 500) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const abortControllerRef = useRef(null);
	const debounceTimerRef = useRef(null);

	// Nettoyer les timers et requêtes à la destruction du composant
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	const search = useCallback(async (searchParam) => {
		// Annuler la requête précédente si elle existe
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Si le paramètre est vide, réinitialiser
		if (!searchParam || searchParam.trim() === '') {
			setData(null);
			setLoading(false);
			return null;
		}

		setLoading(true);
		setError(null);

		// Créer un nouveau AbortController pour cette requête
		abortControllerRef.current = new AbortController();

		try {

			const { url, params} = initFetch(apiEndpoint, HttpMethod.POST);
			const response = await fetch(url, {
				...params,
				body: JSON.stringify({ search: searchParam }),
				signal: abortControllerRef.current.signal,
			});

			if (!response.ok) {
				throw new Error(`Erreur HTTP: ${response.status}`);
			}

			const result = await response.json();

			setData(result);
			return result;
		} catch (err) {
			// Ne pas afficher d'erreur si la requête a été annulée
			if (err.name === 'AbortError') {
				return null;
			}
			setError(err.message);
			return null;
		} finally {
			setLoading(false);
		}
	}, [apiEndpoint]);

	const debouncedSearch = useCallback((searchParam) => {
		// Annuler le timer précédent
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		// Si le champ est vide, réinitialiser immédiatement
		if (!searchParam || searchParam.trim() === '') {
			setData(null);
			setLoading(false);
			return;
		}

		// Afficher l'état de chargement pendant le debounce
		setLoading(true);

		// Créer un nouveau timer
		debounceTimerRef.current = setTimeout(() => {
			search(searchParam);
		}, debounceDelay);
	}, [search, debounceDelay]);

	const reset = useCallback(() => {
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		setData(null);
		setError(null);
		setLoading(false);
	}, []);

	return {
		data,
		loading,
		error,
		search, // Recherche immédiate
		debouncedSearch, // Recherche avec debounce
		reset,
		// Raccourcis pour accéder facilement aux données
		results: data?.result || [],
		count: data?.count || 0,
		status: data?.status || false,
	};
};

export default useSearch;